import {Action, Selector, State, StateContext, Store} from "@ngxs/store";
import {ServiceProviderDialogAction} from "./service-provider-dialog.action";
import {finalize, Observable, throwError} from "rxjs";
import {map} from "rxjs/operators";
import {ServiceProviderRequestService} from "../../services/request/service-provider-request.service";
import {ServiceProviderModel} from "../../models/service_provider/service-provider.model";
import {ResetStateDefaults} from "../base-action-class.model";
import {Injectable} from "@angular/core";

interface ServiceProviderDialogStateModel {
  active: boolean;
  serviceProvider: ServiceProviderModel;
  serviceProviderId: string;
  updatedServiceProvider: ServiceProviderModel;
  inProgress: boolean;
}

const getDefaults = (): ServiceProviderDialogStateModel => ({
  active: false,
  serviceProvider: null,
  serviceProviderId: null,
  updatedServiceProvider: null,
  inProgress: false
});

@State<ServiceProviderDialogStateModel>({
  name: 'serviceProviderDialog',
  defaults: getDefaults()
})
@Injectable()
export class ServiceProviderDialogXsState {

  //region CONSTRUCTOR
  constructor(
    private serviceProviderRequestService: ServiceProviderRequestService,
    private store: Store
  ) {}
  //endregion

  //region SELECTOR
  @Selector()
  static active(state: ServiceProviderDialogStateModel): boolean {
    return state.active;
  }

  @Selector()
  static inProgress(state: ServiceProviderDialogStateModel): boolean {
    return state.inProgress;
  }

  @Selector()
  static serviceProvider(state: ServiceProviderDialogStateModel): ServiceProviderModel {
    return state.serviceProvider;
  }
  //endregion

  //region ACTION
  @Action(ServiceProviderDialogAction.Create)
  create(ctx: StateContext<ServiceProviderDialogStateModel>): void {
    const state: ServiceProviderDialogStateModel = ctx.getState();

    if (state.active) {
      throw new Error('State is already active!');
    }

    ctx.patchState({
      active: true,
      serviceProvider: null,
    });
  }

  @Action(ServiceProviderDialogAction.UpdateState)
  updateState(
    ctx: StateContext<ServiceProviderDialogStateModel>,
    action: ServiceProviderDialogAction.UpdateState
  ): void {
    const state: ServiceProviderDialogStateModel = ctx.getState();

    if (!state.active) {
      throw new Error('State is not active!');
    }

    if (state.inProgress) {
      throw new Error('Request is in progress!');
    }

    ctx.patchState({updatedServiceProvider: action.serviceProvider});
  }

  @Action(ServiceProviderDialogAction.Save)
  save(ctx: StateContext<ServiceProviderDialogStateModel>): Observable<void> {
    const state: ServiceProviderDialogStateModel = ctx.getState();

    if (!state.active) {
      throw new Error('State is not active!');
    }

    if (state.inProgress) {
      throw new Error('Request is in progress!');
    }

    ctx.patchState({inProgress: true});

    const request = this.serviceProviderRequestService
      .createServiceProvider(state.updatedServiceProvider);

    ctx.patchState({inProgress: true});

    return request
      .pipe(
        map(() => {
          this.store.dispatch(new ServiceProviderDialogAction.Close());
        }),
        finalize(() => ctx.patchState({inProgress: false}))
      );
  }

  @Action(ServiceProviderDialogAction.Delete)
  delete(ctx: StateContext<ServiceProviderDialogStateModel>): Observable<void> {
    const state: ServiceProviderDialogStateModel = ctx.getState();

    if (!state.active) {
      return throwError(() => new Error('State is not active!'));
    }

    if (state.inProgress) {
      return throwError(() => new Error('Request is in progress!'));
    }

    ctx.patchState({inProgress: true});

    return this.serviceProviderRequestService.deleteServiceProvider(state.serviceProviderId)
      .pipe(
        map(() => {
          this.store.dispatch(new ServiceProviderDialogAction.Close());
        }),
        finalize(() => ctx.patchState({inProgress: false}))
      );
  }


  @Action([
    ServiceProviderDialogAction.Close,
    ServiceProviderDialogAction.Reset,
    ResetStateDefaults
  ])
  reset(ctx: StateContext<ServiceProviderDialogStateModel>): void {
    ctx.setState(getDefaults);
  }

  //endregion
}

